import * as React from "react";
import { ConfirmationDialogResult, GroupPermissionLevel } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../../framework/appUtils";
import { SaveDataObj } from "../../../../../framework/saveData";
import { IUnitNode } from "../../../../../models/unit/unitNode";
import { IUnitPermissionUserGroup } from "../../../../../models/userGroup/unitPermissionUserGroup";
import { Dialog } from "../../../Dialog/Dialog";
import { Alert, Box, Checkbox, FormControlLabel, Grid, Radio, Stack } from "@mui/material";
import { ICompanyListItem } from "../../../../../models/company/companyListItem";
import { GridRow } from "../../../Grid/GridRow";
import { PanelTitle } from "../../../Panel/PanelTitle";
import { UnitGroupPermissionDialogPartnerCompanyList } from "../../UnitGroupPermissionDialogPartnerCompanyList";
import { TUnitPermission } from "source/models/unit/unitPermission";

export interface IUnitGroupPermission {
    permission: number;
    additionalPermissionA: number;
    additionalPermissionB: number;
    additionalPermissionC: number;
    title: string;
}

export interface IUnitGroupPermissionDialogNewProp {
    classes?: string;
    unitNode: IUnitNode;
    companyGroup: IUnitPermissionUserGroup;
    userGroup: IUnitPermissionUserGroup;
    unitPermissions: TUnitPermission[];
    partnerCompaniesTitle?: string;
    partnerCompanies?: ICompanyListItem[];
    onOk: (permission: number, additionalPermissionA: number, additionalPermissionB: number, additionalPermissionC: number, clearSubUnitPermissions: boolean, copyPermissionToAllSameLevelUnits: boolean, partnerIds: string[]) => void;
    onRemove: () => void;
    onCancel: () => void;
}

export interface IUnitGroupPermissionDialogNewState {
    unitPartnerCompanies: ICompanyListItem[];
    partnerCompanies: ICompanyListItem[];
    permission: number;
    nonExplicitPermission: number;
    additionalPermission: number[];
    hasSiblings: boolean;
    clearSubUnitPermissions: boolean;
    copyPermissionToAllSameLevelUnits: boolean;
}

export class UnitGroupPermissionDialogNew extends React.Component<IUnitGroupPermissionDialogNewProp, IUnitGroupPermissionDialogNewState> {
    private orgStateHash: string = "";

    constructor(props: IUnitGroupPermissionDialogNewProp) {
        super(props);
        const nonExplicitGroupPermission = props.unitNode.getEffectiveNonExplicitPermission(props.companyGroup, props.userGroup);
        const effectiveUnitNodeCompanyIds = props.unitNode.getEffectiveUnitNodeCompanyIds(props.userGroup);
        this.state = {
            unitPartnerCompanies: (props.partnerCompanies ?? []).filter(i => props.unitNode.partnerCompanyIds.indexOf(i.id) > -1),
            permission: Math.min(props.userGroup ? props.unitNode.permission.permission : props.unitNode.permission.companyGroupPermission, Math.min(props.unitNode.maxPermissionLevel, GroupPermissionLevel.Write)),
            nonExplicitPermission: Math.min(props.userGroup ? nonExplicitGroupPermission.permission : nonExplicitGroupPermission.companyGroupPermission, GroupPermissionLevel.Write),
            additionalPermission: [
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionA : props.unitNode.permission.companyGroupAdditionalPermissionA, Math.min(props.unitNode.maxPermissionLevelA, 1)),
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionB : props.unitNode.permission.companyGroupAdditionalPermissionB, Math.min(props.unitNode.maxPermissionLevelB, 1)),
                Math.min(props.userGroup ? props.unitNode.permission.additionalPermissionC : props.unitNode.permission.companyGroupAdditionalPermissionC, Math.min(props.unitNode.maxPermissionLevelC, 1))
            ],
            partnerCompanies: (props.partnerCompanies ?? []).filter(i => effectiveUnitNodeCompanyIds.companyIds.indexOf(i.id) > -1),
            hasSiblings: !props.unitNode.getIsRoot() && props.unitNode.hasSiblings(),
            clearSubUnitPermissions: false,
            copyPermissionToAllSameLevelUnits: false
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    // #region Partner Companies
    handleCompaniesRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            partnerCompanies: this.state.partnerCompanies.filter(i => ids.indexOf(i.id) < 0)
        });
    };
    
    handleCompaniesAdded = (newCompanies: ICompanyListItem[]) => {
        const state = this.state;
        this.setState({
            partnerCompanies: state.partnerCompanies.filter(i => newCompanies.findIndex(j => j.id === i.id) < 0).concat(newCompanies)
        });
    };
    // #endregion Partner Companies

    handleSetPermission = (checked: boolean, permission: number, levelA: number, levelB: number, levelC: number) => {
        if (!checked) return;
        this.setState({
            permission,
            additionalPermission: [levelA, levelB, levelC]
        });
    };

    handleSetClearSubUnitPermissions = (checked: boolean) => {
        this.setState({
            clearSubUnitPermissions: checked
        });
    };

    handleSetCopyPermissionToAllSameLevelUnits = (checked: boolean) => {
        this.setState({
            copyPermissionToAllSameLevelUnits: checked
        });
    };

    getSaveDataFromState = (props: IUnitGroupPermissionDialogNewProp, state: IUnitGroupPermissionDialogNewState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("permission", state.permission);
        result.append("additionalPermissionA", state.additionalPermission[0]);
        result.append("additionalPermissionB", state.additionalPermission[1]);
        result.append("additionalPermissionC", state.additionalPermission[2]);
        result.append("partnerCompanyIds", state.partnerCompanies.map(i => i.id));
        result.append("clearSubUnitPermissions", state.clearSubUnitPermissions);
        result.append("copyPermissionToAllSameLevelUnits", state.copyPermissionToAllSameLevelUnits);
        return result;
    };

    save = async () => {
        const props = this.props;
        const state = this.state;
        props.onOk(
            state.permission,
            state.additionalPermission[0],
            state.additionalPermission[1],
            state.additionalPermission[2],
            state.clearSubUnitPermissions,
            state.copyPermissionToAllSameLevelUnits,
            state.partnerCompanies.map(i => i.id)
        );
    };

    handleSave = () => {
        this.save();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.save();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const valueKey = state.permission + "#" + state.additionalPermission[0] + "#" + state.additionalPermission[1] + "#" + state.additionalPermission[2];
        const permissionDescription = props.unitPermissions.find(i => i.level === state.permission && i.levelA === state.additionalPermission[0] && i.levelB === state.additionalPermission[1] && i.levelC === state.additionalPermission[2])?.description;
        return (
            <Dialog
                id="unit-group-permission-dialog"
                title={props.unitNode.name}
                body={
                    <Box sx={{ margin: "0 0 -1rem" }}>
                        <GridRow>
                            <Grid item sm={12}>
                                <PanelTitle title={Translations.Permission} />
                                <Stack>
                                    {props.unitPermissions.map(i => {
                                        const permissionKey = i.level + "#" + i.levelA + "#" + i.levelB + "#" + i.levelC;
                                        return (
                                            <FormControlLabel
                                                key={permissionKey}
                                                label={i.title}
                                                control={
                                                    <Radio
                                                        checked={permissionKey === valueKey}
                                                        onChange={(_e, checked: boolean) => this.handleSetPermission(checked, i.level, i.levelA, i.levelB, i.levelC)}
                                                    />
                                                }
                                                sx={{ marginBottom: 0 }}
                                            />
                                        );
                                    })}
                                </Stack>
                                {permissionDescription &&
                                    <Alert
                                        severity="info"
                                        sx={{
                                            "marginTop": ".5rem",
                                            "whiteSpace": "pre-line",
                                            ".MuiAlert-message": {
                                                width: "100%"
                                            }
                                        }}>
                                        {permissionDescription}
                                    </Alert>
                                }
                            </Grid>
                            {(state.unitPartnerCompanies.length > 0 || state.partnerCompanies.length > 0) &&
                                <Grid item sm={12}>
                                    <UnitGroupPermissionDialogPartnerCompanyList
                                        availableCompanies={state.unitPartnerCompanies}
                                        companies={state.partnerCompanies}
                                        title={props.partnerCompaniesTitle ?? Translations.Partners}
                                        onRemoved={this.handleCompaniesRemoved}
                                        onAdded={this.handleCompaniesAdded}
                                    />
                                </Grid>
                            }
                            <Grid item sm={12}>
                                {(props.unitNode.hasChildren() || state.hasSiblings) &&
                                    <PanelTitle title={Translations.AdditionalSettings} />
                                }
                                {props.unitNode.hasChildren() &&
                                    <FormControlLabel
                                        label={Translations.ClearSubUnitPermissions}
                                        control={
                                            <Checkbox
                                                checked={state.clearSubUnitPermissions}
                                                onChange={(_e, v) => this.handleSetClearSubUnitPermissions(v)}
                                            />
                                        }
                                    />
                                }
                                {state.hasSiblings &&
                                    <FormControlLabel
                                        label={Translations.CopyPermissionToAllSameLevelUnits}
                                        control={
                                            <Checkbox
                                                checked={state.copyPermissionToAllSameLevelUnits}
                                                onChange={(_e, v) => this.handleSetCopyPermissionToAllSameLevelUnits(v)}
                                            />
                                        }
                                    />
                                }
                            </Grid>
                        </GridRow>
                    </Box>
                }
                buttons={[
                    { title: Translations.Remove, variant: "danger", onClick: props.unitNode.hasExplicitPermission(null, props.userGroup.code) && !(props.unitNode.getIsRoot() && !props.userGroup.code) ? props.onRemove : null },
                    { title: Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: this.handleSave },
                ]}
                onClose={this.handleCancel}/>
        );
    }
}